import React, { useCallback, useEffect, useState } from 'react';
import SupportPlanNotice from '../components/SupportPlanNotice';

/**
 * Portal client Support page (tracker #51 slice 4, support-desk-design.md
 * component 9): the tenant's ESCALATED/DIRECT tickets only. Tier-0 requests
 * live in the customer's SharePoint, are worked by their own administrators
 * inside Edinshall Audit, and never appear here — by design, not by
 * filtering.
 */

interface SupportTicketSummary {
  ticketId: string;
  subject: string;
  origin: string;
  category: string | null;
  status: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  closedAtUtc: string | null;
}

interface SupportTicketAttachment {
  attachmentId: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
  quarantineStatus: string;
  createdAtUtc: string;
}

interface SupportTicketMessage {
  messageId: string;
  authorType: string;
  authorName: string | null;
  body: string;
  fromTierZero: boolean;
  createdAtUtc: string;
  attachments?: SupportTicketAttachment[];
}

interface SupportTicketDetail extends SupportTicketSummary {
  messages: SupportTicketMessage[];
  ticketAttachments?: SupportTicketAttachment[];
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  triaged: 'Triaged',
  waiting_on_client: 'Waiting on you',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed'
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

const statusPillClass = (status: string): string => {
  if (status === 'waiting_on_client') return 'pill warning';
  if (status === 'resolved' || status === 'closed') return 'badge-subtle';
  return 'pill success';
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { message?: string };
  return body.message ?? fallback;
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const Support = (): JSX.Element => {
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<SupportTicketDetail>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const loadTickets = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch('/api/support/tickets');
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      const body = (await response.json()) as { tickets?: SupportTicketSummary[] };
      setTickets(Array.isArray(body.tickets) ? body.tickets : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  const openTicket = useCallback(async (ticketId: string): Promise<void> => {
    setDetailLoading(true);
    setError(undefined);
    setNotice(undefined);
    setReply('');
    setReplyFiles([]);
    try {
      const response = await fetch(`/api/support/tickets/${encodeURIComponent(ticketId)}`);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      const body = (await response.json()) as { ticket?: SupportTicketDetail };
      setDetail(body.ticket);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const sendReply = async (): Promise<void> => {
    if (!detail || !reply.trim()) {
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      const attachments = await Promise.all(
        replyFiles.map(async (file) => ({
          fileName: file.name,
          contentType: file.type || null,
          base64: await readFileAsBase64(file)
        }))
      );
      const response = await fetch(`/api/support/tickets/${encodeURIComponent(detail.ticketId)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: reply.trim(), attachments })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      setReply('');
      setReplyFiles([]);
      await openTicket(detail.ticketId);
      await loadTickets();
      // After openTicket, which resets any prior notice for the fresh view.
      setNotice('Reply sent to Edinshall Customer Support.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const closeCurrentTicket = async (): Promise<void> => {
    if (!detail) {
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/support/tickets/${encodeURIComponent(detail.ticketId)}/close`, {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Support API returned ${response.status}`));
      }
      await openTicket(detail.ticketId);
      await loadTickets();
      setNotice('Ticket closed.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <h1 className="section-title">Support</h1>
      <p className="muted">
        Tickets your organisation escalated (or raised directly) to Edinshall Customer Support.
        Requests raised inside Edinshall Audit are handled by your own administrators in the app
        and never appear here — only what your escalation-permitted admins chose to send leaves
        your tenant.
      </p>
      {error && <div className="pill warning">{error}</div>}
      {notice && <div className="pill success">{notice}</div>}

      {!detail && <SupportPlanNotice />}

      {!detail && (
        <div className="card" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0 }}>Your tickets</h3>
            <button type="button" className="btn secondary" onClick={() => void loadTickets()} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {loading && <span className="badge-subtle" style={{ marginTop: '10px' }}>Loading tickets…</span>}
          {!loading && tickets.length === 0 && (
            <p className="muted" style={{ marginTop: '10px' }}>
              No escalated tickets yet. When your escalation-permitted administrators send a request
              to Edinshall from inside Edinshall Audit, it appears here.
            </p>
          )}
          <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
            {tickets.map((ticket) => (
              <div
                key={ticket.ticketId}
                className="list-row"
                role="button"
                tabIndex={0}
                onClick={() => void openTicket(ticket.ticketId)}
                onKeyDown={(e) => e.key === 'Enter' && void openTicket(ticket.ticketId)}
              >
                <div>
                  <strong>{ticket.subject}</strong>
                  <div className="muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                    {ticket.origin === 'escalated' ? 'Escalated from your Edinshall Audit installation' : 'Raised directly'}
                    {ticket.category ? ` · ${ticket.category}` : ''}
                    {' · Updated '}
                    {new Date(ticket.updatedAtUtc).toLocaleString()}
                  </div>
                </div>
                <span className={statusPillClass(ticket.status)}>{statusLabel(ticket.status)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detailLoading && <div className="badge-subtle" style={{ marginTop: '12px' }}>Loading ticket…</div>}

      {detail && !detailLoading && (
        <div className="card" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <div>
              <h3 style={{ marginBottom: '4px' }}>{detail.subject}</h3>
              <p className="muted" style={{ margin: 0 }}>
                <span className={statusPillClass(detail.status)}>{statusLabel(detail.status)}</span>
                {' · Opened '}
                {new Date(detail.createdAtUtc).toLocaleString()}
                {detail.closedAtUtc ? ` · Closed ${new Date(detail.closedAtUtc).toLocaleString()}` : ''}
              </p>
            </div>
            <button type="button" className="btn secondary" onClick={() => setDetail(undefined)}>
              Back to tickets
            </button>
          </div>

          {(detail.ticketAttachments ?? []).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              {(detail.ticketAttachments ?? []).map((attachment) =>
                attachment.quarantineStatus === 'released' ? (
                  <a
                    key={attachment.attachmentId}
                    className="badge-subtle"
                    href={`/api/support/tickets/${encodeURIComponent(detail.ticketId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download`}
                  >
                    {attachment.fileName} ({formatBytes(attachment.sizeBytes)})
                  </a>
                ) : (
                  <span key={attachment.attachmentId} className="badge-subtle" title="This file is held until malware scanning clears it.">
                    {attachment.fileName} — held for scanning
                  </span>
                )
              )}
            </div>
          )}

          <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
            {detail.messages.map((message) => {
              const isStaff = message.authorType === 'staff' || message.authorType === 'system';
              return (
                <div
                  key={message.messageId}
                  style={{
                    border: '1px solid var(--border)',
                    borderLeft: isStaff ? '3px solid var(--accent)' : '3px solid var(--border)',
                    borderRadius: '4px',
                    background: 'var(--panel)',
                    padding: '10px 12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <strong>
                      {isStaff
                        ? 'Edinshall Support'
                        : (message.authorName || 'Your organisation')}
                    </strong>
                    <span className="muted">
                      {message.fromTierZero ? 'From escalated conversation · ' : ''}
                      {new Date(message.createdAtUtc).toLocaleString()}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{message.body}</p>
                  {(message.attachments ?? []).length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {(message.attachments ?? []).map((attachment) =>
                        attachment.quarantineStatus === 'released' ? (
                          <a
                            key={attachment.attachmentId}
                            className="badge-subtle"
                            href={`/api/support/tickets/${encodeURIComponent(detail.ticketId)}/attachments/${encodeURIComponent(attachment.attachmentId)}/download`}
                          >
                            {attachment.fileName} ({formatBytes(attachment.sizeBytes)})
                          </a>
                        ) : (
                          <span key={attachment.attachmentId} className="badge-subtle" title="This file is held until malware scanning clears it.">
                            {attachment.fileName} — held for scanning
                          </span>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {detail.status !== 'closed' && (
            <div style={{ display: 'grid', gap: '8px', marginTop: '12px', maxWidth: '640px' }}>
              <textarea
                className="input"
                rows={4}
                placeholder="Reply to Edinshall Support"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                disabled={working}
              />
              <label className="muted" style={{ display: 'grid', gap: '4px' }}>
                Attach files (scanned before they become downloadable)
                <input
                  // Remounts when the thread refreshes so a sent reply's file
                  // selection does not linger in the control.
                  key={detail.messages.length}
                  type="file"
                  multiple
                  disabled={working}
                  onChange={(e) => setReplyFiles(Array.from(e.target.files ?? []))}
                />
              </label>
              {replyFiles.length > 0 && (
                <div className="muted" style={{ fontSize: '0.85rem' }}>
                  {replyFiles.map((file) => `${file.name} (${formatBytes(file.size)})`).join(' · ')}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" className="cta" onClick={() => void sendReply()} disabled={working || !reply.trim()}>
                  {working ? 'Working…' : 'Send reply'}
                </button>
                <button type="button" className="btn secondary" onClick={() => void closeCurrentTicket()} disabled={working}>
                  Close ticket
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Support;
